import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags/useFlags";
import { applyColorSchemeClass, persistColorScheme, readStoredColorScheme, type ColorScheme } from "@/lib/colorScheme";

type ColorSchemeContextValue = {
  colorScheme: ColorScheme;
  setColorScheme: (scheme: ColorScheme) => void;
};

const ColorSchemeContext = createContext<ColorSchemeContextValue>({
  colorScheme: "light",
  setColorScheme: () => {}
});

export function useColorScheme() {
  return useContext(ColorSchemeContext);
}

export default function ColorSchemeProvider({ children }: { children: ReactNode }) {
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const enabled = flagsLoaded && flags["backoffice-system-preferences"] === true;
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>("light");

  useEffect(() => {
    if (!flagsLoaded) {
      return;
    }

    if (!enabled) {
      setColorSchemeState("light");
      applyColorSchemeClass("light");
      return () => applyColorSchemeClass("light");
    }

    const stored = readStoredColorScheme();
    setColorSchemeState(stored);
    applyColorSchemeClass(stored);
    return () => applyColorSchemeClass("light");
  }, [enabled, flagsLoaded]);

  const setColorScheme = useCallback(
    (scheme: ColorScheme) => {
      if (!enabled) return;
      setColorSchemeState(scheme);
      persistColorScheme(scheme);
      applyColorSchemeClass(scheme);
    },
    [enabled]
  );

  const value = useMemo(() => ({ colorScheme, setColorScheme }), [colorScheme, setColorScheme]);

  return <ColorSchemeContext.Provider value={value}>{children}</ColorSchemeContext.Provider>;
}
